import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Search, Filter } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { supabase } from '@/lib/supabase/client'
import { cn } from '@/lib/utils/cn'

export const EVENT_TYPE_LABELS: Record<string, string> = {
  // Records
  record_created:               'ایجاد سابقه',
  record_status_changed:        'تغییر وضعیت',
  record_final_submitted:       'ارسال نهایی',
  record_under_review:          'شروع بررسی',
  record_approved:              'تأیید سابقه',
  record_declined:              'رد سابقه',
  record_resumed_after_decline: 'از سر گرفتن',
  record_soft_deleted:          'حذف سابقه',
  record_restored:              'بازیابی سابقه',
  record_updated:               'ویرایش سابقه',
  record_form_version_upgraded: 'ارتقاء نسخه فرم',
  // Submissions
  form_draft_saved:             'ذخیره پیش‌نویس',
  form_submitted:               'ارسال فرم',
  form_resubmitted:             'ارسال مجدد فرم',
  field_value_changed:          'تغییر مقدار فیلد',
  // Legacy
  submission_saved:             'ذخیره فرم',
  submission_submitted:         'ارسال فرم',
  // Form versioning
  form_published:                'انتشار فرم',
  form_minor_version_published:  'انتشار نسخه جزئی',
  form_major_version_published:  'انتشار نسخه اصلی',
  // Comments
  comment_added:        'افزودن نظر',
  comment_reply_added:  'پاسخ به نظر',
  comment_resolved:     'حل نظر',
  comment_reopened:     'بازگشایی نظر',
  comment_edited:       'ویرایش نظر',
  comment_deleted:      'حذف نظر',
  // Members
  member_added:        'افزودن عضو',
  member_removed:      'حذف عضو',
  member_role_changed: 'تغییر نقش عضو',
  // Project
  project_updated: 'ویرایش پروژه',
}

export const TARGET_TYPE_LABELS: Record<string, string> = {
  record:       'سابقه',
  submission:   'فرم',
  comment:      'نظر',
  project:      'پروژه',
  form:         'فرم',
  form_group:   'گروه فرم',
  field:        'فیلد',
  member:       'عضو',
  note:         'یادداشت',
  file:         'فایل',
  summary_card: 'کارت خلاصه',
  export_job:   'خروجی',
  system:       'سیستم',
}

async function fetchAuditEntries(
  projectId: string,
  opts: { eventType?: string; search?: string; page: number }
) {
  let q = supabase
    .from('audit_entries')
    .select(`
      id, audit_seq, event_type, target_type, target_id, description, created_at,
      before_value, after_value,
      actor_user_id,
      user_profiles!actor_user_id(full_name),
      record_id
    `)
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
    .range(opts.page * 50, opts.page * 50 + 49)

  if (opts.eventType && opts.eventType !== 'all') {
    q = q.eq('event_type', opts.eventType)
  }

  const { data, error } = await q
  if (error) throw error
  return data ?? []
}

export function AuditLogPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const [search, setSearch] = useState('')
  const [eventTypeFilter, setEventTypeFilter] = useState('all')
  const [page, setPage] = useState(0)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['audit-log', projectId, eventTypeFilter, page],
    queryFn: () => fetchAuditEntries(projectId!, { eventType: eventTypeFilter, page }),
    enabled: !!projectId,
  })

  const filtered = search
    ? entries.filter(
        (e) =>
          e.description?.includes(search) ||
          e.event_type.includes(search) ||
          e.target_id?.includes(search)
      )
    : entries

  const toggleExpand = (id: string) => {
    const next = new Set(expandedIds)
    next.has(id) ? next.delete(id) : next.add(id)
    setExpandedIds(next)
  }

  const formatTime = (ts: string) => {
    try {
      return new Date(ts).toLocaleString('fa-IR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    } catch {
      return ts
    }
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="تاریخچه تغییرات"
        breadcrumbs={[{ label: 'تاریخچه تغییرات' }]}
      />

      <div className="flex-1 overflow-y-auto">
        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-border flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="جستجو در توضیحات..."
              className="ps-9"
            />
          </div>
          <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
            <SelectTrigger className="w-48">
              <Filter className="w-4 h-4 me-2 text-muted-foreground" />
              <SelectValue placeholder="نوع رویداد" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">همه رویدادها</SelectItem>
              {Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-start px-4 py-3 text-xs font-medium text-muted-foreground w-8"></th>
                <th className="text-start px-4 py-3 text-xs font-medium text-muted-foreground">#</th>
                <th className="text-start px-4 py-3 text-xs font-medium text-muted-foreground">زمان</th>
                <th className="text-start px-4 py-3 text-xs font-medium text-muted-foreground">کاربر</th>
                <th className="text-start px-4 py-3 text-xs font-medium text-muted-foreground">رویداد</th>
                <th className="text-start px-4 py-3 text-xs font-medium text-muted-foreground">هدف</th>
                <th className="text-start px-4 py-3 text-xs font-medium text-muted-foreground">توضیح</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading && Array.from({ length: 10 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={7} className="px-4 py-3">
                    <div className="h-4 bg-muted rounded animate-pulse" />
                  </td>
                </tr>
              ))}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    رویداد یافت نشد
                  </td>
                </tr>
              )}
              {filtered.map((entry) => {
                const isExpanded = expandedIds.has(entry.id)
                const actor = entry.user_profiles as { full_name?: string } | null
                const hasDiff = entry.before_value || entry.after_value

                return (
                  <>
                    <tr
                      key={entry.id}
                      className={cn(
                        'hover:bg-muted/20 transition-colors',
                        isExpanded && 'bg-muted/10'
                      )}
                    >
                      <td className="px-4 py-3">
                        {hasDiff && (
                          <button
                            onClick={() => toggleExpand(entry.id)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            {isExpanded
                              ? <ChevronDown className="w-4 h-4" />
                              : <ChevronRight className="w-4 h-4 rotate-180" />
                            }
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                        {entry.audit_seq}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {formatTime(entry.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {actor?.full_name ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          {EVENT_TYPE_LABELS[entry.event_type] ?? entry.event_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className="text-muted-foreground">
                          {TARGET_TYPE_LABELS[entry.target_type] ?? entry.target_type}
                        </span>
                        {entry.record_id && (
                          <Link
                            to={`/projects/${projectId}/records/${entry.record_id}`}
                            className="ms-2 text-primary hover:underline"
                          >
                            ↗
                          </Link>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">
                        {entry.description ?? '—'}
                      </td>
                    </tr>
                    {isExpanded && hasDiff && (
                      <tr key={`${entry.id}-detail`} className="bg-muted/5">
                        <td colSpan={7} className="px-8 py-4">
                          <div className="grid grid-cols-2 gap-4">
                            {entry.before_value && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-2">قبل</p>
                                <pre className="text-xs bg-red-500/5 border border-red-500/20 rounded-lg p-3 overflow-auto max-h-48 font-mono text-red-300 text-start">
                                  {JSON.stringify(entry.before_value, null, 2)}
                                </pre>
                              </div>
                            )}
                            {entry.after_value && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-2">بعد</p>
                                <pre className="text-xs bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 overflow-auto max-h-48 font-mono text-emerald-300 text-start">
                                  {JSON.stringify(entry.after_value, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            صفحه {(page + 1).toLocaleString('fa-IR')}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              قبلی
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={filtered.length < 50}
              onClick={() => setPage((p) => p + 1)}
            >
              بعدی
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
